/**
 * orbitNotification — pure routing logic for the "Lev added N people for your
 * Orbit" notification click (LEV-1070), reconciled with LEV-1044.
 *
 * The decision is **context-dependent on where the user currently is**, not on
 * which project the import was for:
 *
 * - Inside a project (the canvas route has a `projectId`) → open *that*
 *   project's Orbit lightbox inline. We deliberately do NOT navigate away from
 *   the project the founder is working in.
 * - On the home page / anywhere without a project → soft-navigate to the global
 *   Orbit People page with the Import history modal open (LEV-1044's surface).
 *
 * Either way the in-app click is client-side — no hard refresh. The static
 * Courier `url` (`importHistoryUrl()`) remains the email/out-of-app fallback;
 * NotificationBell intercepts in-app clicks rather than following it blindly.
 *
 * Pure + DOM-free so the routing contract is unit-testable directly.
 */

import {
  IMPORT_HISTORY_QUERY_PARAM,
  IMPORT_HISTORY_QUERY_VALUE,
} from "@/lib/people/importJob";

/**
 * Client-side path to the global Orbit People page with the Import history
 * modal pre-opened (LEV-1044). Reuses the same query contract the server's
 * `importHistoryUrl()` and `PeopleDbView` agree on.
 */
export const IMPORT_HISTORY_PATH = `/orbit?${IMPORT_HISTORY_QUERY_PARAM}=${IMPORT_HISTORY_QUERY_VALUE}`;

export type OrbitNotificationAction =
  | { mode: "inline"; projectId: string }
  | { mode: "navigate"; path: string };

/**
 * Decide how to open Orbit for a people-import notification click, given the
 * project the user is currently viewing (from `useParams().projectId`, or null
 * when not on a project route). Always returns an action — there's a valid
 * destination in both contexts.
 */
export function resolveOrbitNotificationAction(args: {
  currentProjectId: string | null | undefined;
}): OrbitNotificationAction {
  const { currentProjectId } = args;
  if (currentProjectId) return { mode: "inline", projectId: currentProjectId };
  return { mode: "navigate", path: IMPORT_HISTORY_PATH };
}
